import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { DateRangeField } from './DateRangeField';
import { getErrorMessage } from '../api/client';
import { useConfirm } from './ui/ConfirmDialog';
import { createRecipe, getRecipeCoverage, linkRecipeLoyverse, unlinkRecipeLoyverse } from '../api/inventory';
import { useAuth } from '../auth/AuthProvider';
import { getFinancialStoreOptions } from '../auth/permissions';
import type { Recipe, RecipeCoverageFilters, RecipeCoverageItem, RecipeCoverageResponse, RecipeCoverageStatus } from '../types/inventory';
import { AccessDeniedPage } from '../pages/AccessDeniedPage';
import { DEFAULT_STORE_TIMEZONE, dateToStoreISO } from '../utils/timezones';
import { useCardRows } from '../utils/dataTableCards';
import { useLoyversePresence } from '../utils/loyversePresence';
import { Modal } from './Modal';

const PAGE_SIZE = 25;

interface RecipeCoveragePanelProps {
  recipes: Recipe[];
  onOpenRecipe: (recipeId: string) => void;
  onRecipesChanged: () => Promise<void>;
}

export function RecipeCoveragePanel({ recipes, onOpenRecipe, onRecipesChanged }: RecipeCoveragePanelProps) {
  const confirm = useConfirm();
  // Patrón de tarjetas en móvil (≤640 px) para la tabla de cobertura.
  const cards = useCardRows();
  const { user, stores: authStores } = useAuth();
  const loyverseConnected = useLoyversePresence();
  // Fase 16B-2: la cobertura de recetas incluye costo estimado por producto — se trata como
  // información financiera igual que el dashboard (ver docs/PERMISSIONS_MODEL.md, brecha 2).
  const financialStores = getFinancialStoreOptions(user?.role, authStores);
  const preselectedStoreId = !financialStores.allowsAllStores && financialStores.selectableStores.length === 1
    ? financialStores.selectableStores[0]!.id
    : undefined;
  const [filters, setFilters] = useState<RecipeCoverageFilters>(() => defaultFilters(preselectedStoreId));
  const [appliedFilters, setAppliedFilters] = useState<RecipeCoverageFilters>(() => defaultFilters(preselectedStoreId));
  const [coverage, setCoverage] = useState<RecipeCoverageResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [linkTarget, setLinkTarget] = useState<RecipeCoverageItem>();
  const [selectedRecipeId, setSelectedRecipeId] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  const loadCoverage = useCallback(async (nextFilters: RecipeCoverageFilters, offset = 0) => {
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await getRecipeCoverage({ ...nextFilters, limit: PAGE_SIZE, offset });
      setCoverage(result);
      setAppliedFilters(nextFilters);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible cargar la cobertura de recetas.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (financialStores.hasNoFinancialAccess) {
      setIsLoading(false);
      return;
    }
    void loadCoverage(defaultFilters(preselectedStoreId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCoverage]);

  if (financialStores.hasNoFinancialAccess) {
    return <AccessDeniedPage
      message="No tienes permiso para ver información financiera. Pídele al Dueño o a un Gerente que revise tus tiendas asignadas."
      onReturn={() => { window.location.hash = 'dashboard'; }}
      title="No tienes permiso para ver la cobertura de recetas"
    />;
  }

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    if (isLoading) return;
    void loadCoverage(filters, 0);
  };

  const startLink = (item: RecipeCoverageItem) => {
    setLinkTarget(item);
    setSelectedRecipeId('');
    setError(undefined);
  };

  const saveLink = async () => {
    if (!linkTarget || !selectedRecipeId || isLinking) return;
    if (!linkTarget.loyverseItemId && !linkTarget.loyverseVariantId) {
      setError('Este producto vendido aún no existe en el catálogo local de Loyverse. Sincroniza el catálogo antes de ligarlo.');
      setLinkTarget(undefined);
      return;
    }
    setIsLinking(true);
    setError(undefined);
    try {
      await linkRecipeLoyverse(selectedRecipeId, {
        ...(linkTarget.loyverseItemId ? { itemId: linkTarget.loyverseItemId } : {}),
        ...(linkTarget.loyverseVariantId ? { variantId: linkTarget.loyverseVariantId } : {}),
      });
      setLinkTarget(undefined);
      setNotice('Receta ligada correctamente.');
      await onRecipesChanged();
      await loadCoverage(appliedFilters, coverage?.pagination.offset ?? 0);
    } catch (linkError) {
      setError(getErrorMessage(linkError, 'No fue posible ligar la receta.'));
    } finally {
      setIsLinking(false);
    }
  };

  // "Crear receta": crea la receta con el nombre del producto, la deja YA vinculada al item/variante
  // de Loyverse y abre su detalle en sitio para capturar los componentes (mismo atajo que la simulación).
  const createAndLink = async (item: RecipeCoverageItem) => {
    if (isLinking) return;
    if (!item.loyverseItemId && !item.loyverseVariantId) {
      setError('Este producto vendido aún no existe en el catálogo local de Loyverse. Sincroniza el catálogo antes de crearle receta.');
      return;
    }
    setIsLinking(true);
    setError(undefined);
    try {
      const name = item.variantName && item.variantName !== item.itemName ? `${item.itemName} ${item.variantName}` : item.itemName;
      const { recipe } = await createRecipe({ name, description: null, outputQuantity: 1, outputUnitId: null });
      await linkRecipeLoyverse(recipe.id, {
        ...(item.loyverseItemId ? { itemId: item.loyverseItemId } : {}),
        ...(item.loyverseVariantId ? { variantId: item.loyverseVariantId } : {}),
      });
      setNotice(`Receta "${name}" creada y ligada. Captura sus componentes.`);
      await onRecipesChanged();
      await loadCoverage(appliedFilters, coverage?.pagination.offset ?? 0);
      onOpenRecipe(recipe.id);
    } catch (createError) {
      setError(getErrorMessage(createError, 'No fue posible crear la receta.'));
    } finally {
      setIsLinking(false);
    }
  };

  const unlink = async (item: RecipeCoverageItem) => {
    if (!item.linkedRecipeId || isLinking || !(await confirm({ title: 'Desligar receta', description: '¿Desligar esta receta del producto Loyverse?', confirmLabel: 'Desligar', danger: true }))) return;
    setIsLinking(true);
    setError(undefined);
    try {
      await unlinkRecipeLoyverse(item.linkedRecipeId);
      setNotice('Receta desligada correctamente.');
      await onRecipesChanged();
      await loadCoverage(appliedFilters, coverage?.pagination.offset ?? 0);
    } catch (unlinkError) {
      setError(getErrorMessage(unlinkError, 'No fue posible desligar la receta.'));
    } finally {
      setIsLinking(false);
    }
  };

  const summary = coverage?.summary;
  const activeRecipes = recipes.filter((recipe) => recipe.isActive);

  // ZTV-03/ZTV-04: esta vista analiza SOLO ventas conectadas desde Loyverse. Sin la integración
  // conectada, mostrar puros ceros y "No hay ventas" a un negocio que sí vendió era engañoso: se
  // declara el alcance y se señala dónde vive el análisis de las ventas registradas aquí.
  if (loyverseConnected === false) {
    return (
      <div className="coverage-panel">
        <div className="empty-panel">
          <strong>Esta vista analiza las ventas conectadas desde Loyverse</strong>
          <span>
            Tu negocio no tiene esa integración conectada, así que aquí no hay nada que revisar.
            El consumo y margen de las ventas registradas en Negocio360 vive en Ventas e ingresos → Consumo y margen.
            Si usas Loyverse, conéctalo en Configuración → Integraciones.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="coverage-panel">
      {error && <div className="alert alert--error">{error}</div>}
      {notice && <div className="alert alert--success">{notice}</div>}

      <form className="coverage-filters" onSubmit={applyFilters}>
        <DateRangeField from={filters.dateFrom || ''} to={filters.dateTo || ''} onChange={(from, to) => setFilters({ ...filters, dateFrom: from, dateTo: to })} />
        <label className="field"><span>Tienda</span><select value={filters.storeId || ''} onChange={(event) => setFilters({ ...filters, storeId: event.target.value || undefined })}>{financialStores.allowsAllStores && <option value="">Todas las tiendas</option>}{!financialStores.allowsAllStores && !filters.storeId && <option value="">Selecciona una tienda</option>}{financialStores.selectableStores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
        <label className="field"><span>Buscar</span><input placeholder="Producto, variante o SKU" value={filters.search || ''} onChange={(event) => setFilters({ ...filters, search: event.target.value || undefined })} /></label>
        <label className="coverage-check"><input checked={Boolean(filters.onlyMissingRecipe)} type="checkbox" onChange={(event) => setFilters({ ...filters, onlyMissingRecipe: event.target.checked, ...(event.target.checked ? { onlyIncompleteCost: false } : {}) })} /><span>Solo sin receta</span></label>
        <label className="coverage-check"><input checked={Boolean(filters.onlyIncompleteCost)} type="checkbox" onChange={(event) => setFilters({ ...filters, onlyIncompleteCost: event.target.checked, ...(event.target.checked ? { onlyMissingRecipe: false } : {}) })} /><span>Solo costo incompleto</span></label>
        <button className="button button--primary" disabled={isLoading || (!financialStores.allowsAllStores && !filters.storeId)} type="submit">{isLoading ? 'Consultando...' : 'Aplicar filtros'}</button>
      </form>
      {!financialStores.allowsAllStores && !filters.storeId && <div className="alert alert--warning">Selecciona una tienda válida para ver su información financiera.</div>}

      {summary && <div className="coverage-summary">
        <CoverageCard label="Productos vendidos únicos" value={summary.uniqueSoldProducts} />
        <CoverageCard label="Con receta" value={summary.mappedProductsCount} />
        <CoverageCard label="Sin receta" value={summary.unmappedProductsCount} />
        <CoverageCard label="Costo completo" value={summary.completeCostProductsCount} />
        <CoverageCard label="Costo incompleto" value={summary.incompleteCostProductsCount} />
        <CoverageCard label="Cobertura por ventas" value={`${summary.mappedCoveragePercentBySales}%`} />
      </div>}

      {summary?.lastReceiptsSyncAt && <p className="coverage-sync">Última actualización de ventas: {formatDateTime(summary.lastReceiptsSyncAt)}</p>}

      {isLoading ? <div className="inline-loading"><span className="spinner" />Calculando cobertura...</div> : coverage && coverage.items.length > 0 ? (
        <>
          {/* Owner Intelligence v1 (spec §17, criterio A7): esta ES la pantalla de la cobertura —
              la que el dueño abre cuando el Inicio le dice que no puede calcular su resultado. Con
              1320 px de tabla era ilegible en el teléfono desde el que la iba a abrir. */}
          <div className="data-table-wrap"><table className="data-table coverage-table data-table--cards dt-analytic"><thead><tr><th>Producto</th><th>Variante</th><th>Cantidad</th><th>Tickets</th><th>Venta bruta</th><th>Receta</th><th>Estado</th><th>Costo estimado</th><th>Alertas</th><th>Acción</th></tr></thead><tbody>{coverage.items.map((item) => {
            const key = coverageKey(item);
            return <tr className={cards.rowClass(key)} key={key} onClick={() => cards.toggle(key)}>
              <td className="dt-card-main"><strong>{item.itemName}</strong><small>{item.variantName || 'General'} · {item.sku || 'Sin SKU'}</small></td>
              <td className="dt-hide-m" data-label="Variante">{item.variantName || 'General'}</td>
              <td className="dt-card-keep" data-label="Cantidad">{formatQuantity(item.quantitySold)}</td>
              <td data-label="Tickets">{item.receiptsCount}</td>
              <td className="dt-card-keep" data-label="Venta bruta">{formatMoney(item.grossSales)}</td>
              <td data-label="Receta">{item.linkedRecipeName || 'Sin receta'}</td>
              <td className="dt-card-keep" data-label="Estado"><CoverageBadge status={item.recipeStatus} /></td>
              <td data-label="Costo estimado">{item.estimatedUnitCost ? `${formatMoney(item.estimatedUnitCost)} / unidad` : 'No disponible'}</td>
              <td data-label="Alertas">{item.warnings.length ? <ul className="coverage-warnings">{item.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul> : '—'}</td>
              <td className="dt-card-actions dt-card-keep"><div className="coverage-actions">{item.linkedRecipeId ? <><button className="text-button" onClick={(event) => { event.stopPropagation(); onOpenRecipe(item.linkedRecipeId!); }} type="button">Ver receta</button><button className="text-button text-button--danger" disabled={isLinking} onClick={(event) => { event.stopPropagation(); void unlink(item); }} type="button">Desligar</button></> : <><button className="text-button" disabled={isLinking || (!item.loyverseItemId && !item.loyverseVariantId)} onClick={(event) => { event.stopPropagation(); void createAndLink(item); }} type="button">Crear receta</button><button className="text-button" disabled={isLinking || (!item.loyverseItemId && !item.loyverseVariantId)} onClick={(event) => { event.stopPropagation(); startLink(item); }} type="button">Ligar existente</button></>}</div></td>
            </tr>;
          })}</tbody></table></div>
          <div className="coverage-pagination"><span>Mostrando {coverage.pagination.offset + 1}–{coverage.pagination.offset + coverage.items.length} de {coverage.pagination.total}</span><div><button className="button button--ghost" disabled={isLoading || coverage.pagination.offset === 0} onClick={() => void loadCoverage(appliedFilters, Math.max(0, coverage.pagination.offset - PAGE_SIZE))} type="button">Anterior</button><button className="button button--ghost" disabled={isLoading || !coverage.pagination.hasMore} onClick={() => void loadCoverage(appliedFilters, coverage.pagination.offset + PAGE_SIZE)} type="button">Siguiente</button></div></div>
        </>
      ) : <div className="empty-panel"><strong>{appliedFilters.onlyMissingRecipe ? 'Todos los productos vendidos ya tienen receta' : 'No hay ventas conectadas desde Loyverse en este rango'}</strong><span>Ajusta las fechas o actualiza las ventas desde Conexión Loyverse.</span></div>}

      {linkTarget && <Modal title="Ligar receta existente" description={`${linkTarget.itemName}${linkTarget.variantName ? ` · ${linkTarget.variantName}` : ''}`} isBusy={isLinking} onClose={() => setLinkTarget(undefined)} footer={<><button className="button button--ghost" disabled={isLinking} onClick={() => setLinkTarget(undefined)} type="button">Cancelar</button><button className="button button--primary" disabled={isLinking || !selectedRecipeId} onClick={() => void saveLink()} type="button">{isLinking ? 'Ligando...' : 'Ligar receta'}</button></>}><label className="field"><span>Receta</span><select value={selectedRecipeId} onChange={(event) => setSelectedRecipeId(event.target.value)}><option value="">Selecciona una receta</option>{activeRecipes.map((recipe) => <option key={recipe.id} value={recipe.id}>{recipe.name}</option>)}</select></label>{activeRecipes.length === 0 && <div className="alert alert--error">No hay recetas activas disponibles. Crea una receta manualmente primero.</div>}</Modal>}
    </div>
  );
}

function CoverageCard({ label, value }: { label: string; value: string | number }) {
  return <article><span>{label}</span><strong>{value}</strong></article>;
}

function CoverageBadge({ status }: { status: RecipeCoverageStatus }) {
  const labels: Record<RecipeCoverageStatus, string> = {
    MAPPED_COMPLETE: 'Costo completo',
    MAPPED_INCOMPLETE_COST: 'Costo incompleto',
    MISSING_RECIPE: 'Sin receta',
    UNKNOWN: 'Sin identificar',
  };
  return <span className={`coverage-badge coverage-badge--${status.toLowerCase()}`}>{labels[status]}</span>;
}

function defaultFilters(storeId?: string): RecipeCoverageFilters {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 6);
  return { dateFrom: toDateInput(from), dateTo: toDateInput(today), storeId, onlyMissingRecipe: false, onlyIncompleteCost: false };
}

function toDateInput(value: Date) { return dateToStoreISO(value); }

function coverageKey(item: RecipeCoverageItem) {
  return item.loyverseVariantId || item.variantExternalId || item.loyverseItemId || item.itemExternalId || `${item.itemName}-${item.variantName}-${item.sku}`;
}

function formatMoney(value: string) { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value)); }
function formatQuantity(value: string) { return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 4 }).format(Number(value)); }
function formatDateTime(value: string) { return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone: DEFAULT_STORE_TIMEZONE }).format(new Date(value)); }
